import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import {
  HotToken,
  apiCreateHotToken,
  apiDeleteHotToken,
  apiGetHotTokens,
  apiUpdateHotToken,
} from "../../../services/HotTokenAPI";
import useActions, { useAppSelector } from "../../../app/hooks";
import { selectLibrary } from "../../library/librarySlice";

export default function HotTokensList(props: {
  parentCloseHandle: () => void;
  workspaceId: number;
}) {
  const { parentCloseHandle, workspaceId } = props;

  const actions = useActions();
  const librarySlice = useAppSelector(selectLibrary);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [hotTokens, setHotTokens] = useState<HotToken[]>([]);
  const [automationId, setAutomationId] = useState<string>("");
  const [fileExtensions, setFileExtensions] = useState("");
  const [regex, setRegex] = useState("");
  const [excludeRegex, setExcludeRegex] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    apiGetHotTokens(workspaceId)
      .then((response) => {
        if (cancelled) return;
        const data =
          (response.data?.hot_tokens ||
            response.data?.HotTokens ||
            response.data) as HotToken[] | undefined;
        setHotTokens(data || []);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Fetch workspace automations so user can select an automation
  useEffect(() => {
    actions.getAutomations();
  }, [actions]);

  function resetEditor() {
    setAutomationId("");
    setFileExtensions("");
    setRegex("");
    setExcludeRegex("");
    setCreateMode(false);
    setEditingId(null);
    setSaving(false);
  }

  function getPayload() {
    const aid = parseInt(automationId, 10);
    if (Number.isNaN(aid)) return null;
    return {
      automation_id: aid,
      file_extensions: fileExtensions,
      regex,
      exclude_regex: excludeRegex,
    };
  }

  async function refreshList() {
    const response = await apiGetHotTokens(workspaceId);
    const data =
      (response.data?.hot_tokens ||
        response.data?.HotTokens ||
        response.data) as HotToken[] | undefined;
    setHotTokens(data || []);
  }

  async function handleSave() {
    const payload = getPayload();
    if (!payload) return;
    try {
      setSaving(true);
      if (editingId === null) {
        await apiCreateHotToken(workspaceId, payload);
        setCreateMode(false);
      } else {
        await apiUpdateHotToken(workspaceId, editingId, payload);
        setEditingId(null);
      }
      await refreshList();
      resetEditor();
      setError(undefined);
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(token: HotToken) {
    setEditingId(token.id);
    setAutomationId(String(token.automation_id));
    setFileExtensions(token.file_extensions ?? "");
    setRegex(token.regex ?? "");
    setExcludeRegex(token.exclude_regex ?? "");
  }

  async function handleDelete(id: number) {
    try {
      setDeletingId(id);
      await apiDeleteHotToken(workspaceId, id);
      await refreshList();
      setError(undefined);
    } catch (e) {
      setError(String(e));
    } finally {
      setDeletingId(null);
    }
  }

  const payload = getPayload();
  const canSave = payload !== null;

  return (
    <Dialog open={true} onClose={() => parentCloseHandle()} maxWidth="sm" fullWidth>
      <DialogTitle>Hot tokens</DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" sx={{ mb: 2 }}>
            Failed to load hot tokens: {error}
          </Typography>
        )}
        {!loading && !error && (
          <Box sx={{ pt: 2 }}>
            {!createMode && !editingId && (
              <>
                {hotTokens.length === 0 && (
                  <Typography sx={{ mb: 2, textAlign: "center" }}>
                    No hot tokens in the workspace...
                  </Typography>
                )}

                {hotTokens.length > 0 && (
                  <List>
                    {hotTokens.map((token) => (
                      <ListItem
                        key={"hot_token_" + token.id}
                        sx={{ py: 1.5 }}
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton
                              edge="end"
                              onClick={() => handleEdit(token)}
                              size="small"
                              title="Edit hot token"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDelete(token.id)}
                              size="small"
                              title="Delete hot token"
                              disabled={deletingId === token.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={`Automation ${token.automation_id} · ${token.file_extensions || "(no ext)"}`}
                          secondary={
                            <>
                              {token.regex && `regex: ${token.regex}`}
                              {token.exclude_regex && ` · exclude: ${token.exclude_regex}`}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}

            {(createMode || editingId !== null) && (
              <Box sx={{ mt: createMode ? 0 : 3 }}>
                <DialogContentText sx={{ mb: 1 }}>
                  {editingId === null
                    ? "Create a new hot token"
                    : "Edit hot token"}
                </DialogContentText>
                <TextField
                  select
                  label="Automation"
                  value={automationId}
                  onChange={(e) => setAutomationId(e.target.value)}
                  fullWidth
                  margin="dense"
                  variant="standard"
                  sx={{ mb: 1 }}
                >
                  <MenuItem value="">
                    <em>Select automation</em>
                  </MenuItem>
                  {librarySlice.automations.map((automation) => (
                    <MenuItem
                      key={`hot_token_automation_${automation.id}`}
                      value={String(automation.id)}
                    >
                      {automation.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="File extensions"
                  value={fileExtensions}
                  onChange={(e) => setFileExtensions(e.target.value)}
                  fullWidth
                  margin="dense"
                  variant="standard"
                  placeholder="e.g. .rb,.py"
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Regex"
                  value={regex}
                  onChange={(e) => setRegex(e.target.value)}
                  fullWidth
                  margin="dense"
                  variant="standard"
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="Exclude regex"
                  value={excludeRegex}
                  onChange={(e) => setExcludeRegex(e.target.value)}
                  fullWidth
                  margin="dense"
                  variant="standard"
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        {!createMode && editingId === null && (
          <>
            <Button
              onClick={() => {
                resetEditor();
                parentCloseHandle();
              }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreateMode(true)}
            >
              CREATE HOT TOKEN
            </Button>
          </>
        )}
        {(createMode || editingId !== null) && (
          <>
            <Button
              onClick={() => {
                resetEditor();
                if (createMode) setCreateMode(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || !canSave}
            >
              {editingId === null ? "Create" : "Update"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
